import React, { useEffect, useId, useState } from 'react';
import './Accordion.css';

// Reusable collapsible row. Each row owns its own open/closed state, so several
// can be open at once and the parent stays a dumb wrapper - no shared state to
// thread through, and rows can be added or reordered freely.
//
// A closed row's content is still in the page, marked `hidden`. That is what a
// crawler, a screen reader's find, and the pre-rendered HTML see: a
// specifications row that only exists once it is clicked reads as an empty
// page to anything that does not run JavaScript. Pass `lazy` for content that
// must not exist until asked for - a video that would otherwise start loading
// or autoplay out of sight.
//
// `headingLevel` sets the row's heading (2 by default). A row nested under a
// section heading - a question in an FAQ - is a level below it, not beside it.
//
// An optional `id` makes the row deep-linkable: a link to #that-id opens the
// row and scrolls to it, both on first load and when the hash changes later.
export function AccordionRow({ title, subtitle, defaultOpen = false, id, lazy = false, headingLevel = 2, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const uid = useId();
  const panelId = `acc-panel-${uid}`;
  const buttonId = `acc-button-${uid}`;
  const Heading = `h${Math.min(6, Math.max(2, headingLevel))}`;

  useEffect(() => {
    if (!id) return undefined;
    const check = () => {
      if (window.location.hash === `#${id}`) {
        setOpen(true);
        const el = document.getElementById(id);
        if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'start', behavior: 'smooth' }));
      }
    };
    check();
    window.addEventListener('hashchange', check);
    return () => window.removeEventListener('hashchange', check);
  }, [id]);

  return (
    <div className={`acc-row${open ? ' acc-row--open' : ''}`} id={id}>
      <Heading className="acc-heading">
        <button
          type="button"
          id={buttonId}
          className="acc-trigger"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="acc-trigger-text">
            <span className="acc-title">{title}</span>
            {subtitle && <span className="acc-subtitle">{subtitle}</span>}
          </span>
          <svg className="acc-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </Heading>
      {(open || !lazy) && (
        <div className="acc-panel" id={panelId} role="region" aria-labelledby={buttonId} hidden={!open}>
          <div className="acc-panel-inner">{children}</div>
        </div>
      )}
    </div>
  );
}

export default function Accordion({ children, className = '' }) {
  return <div className={`acc${className ? ' ' + className : ''}`}>{children}</div>;
}
